/**
 * The context a CSG root builds its plan's solids against: the scene's pools, each `.tres` and each
 * font a shape in its plan reads, and the outlines and metrics of those fonts once they settle. A node inside an
 * ancestor's plan passes null and takes the root's context from `CsgSubtreeContext`, so only the
 * root loads.
 */
import { useCallback, useMemo } from 'react';
import { useSceneFontSettleCount } from '../controls/native/text/useSceneFontSettleCount';
import { useLoadedFilesAndFonts } from '../../resources/useLoadedFilesAndFonts';
import { nodeComponentRegistry } from '../NodeComponentRegistry';
import { useSceneResources } from '../SceneResourcesContext';
export function useCsgGeometryContext(root) {
    const pools = useSceneResources();
    const shapes = useMemo(() => (root ? subtreeShapes(root) : []), [root]);
    const filesPlan = useCallback((loadedFile) => subtreeFiles(shapes, pools, loadedFile), [shapes, pools]);
    // A plan with no shape that can read a font names no font bus, so it listens to none.
    const readsFonts = shapes.some(({ shape }) => shape.readsFonts);
    const fontsPlan = useCallback((loadedFont, loadedFile, projectFonts) => subtreeFontFiles(shapes, { ...pools, loadedFile, loadedFont, projectFonts }), [shapes, pools]);
    const { loadedFile, loadedFont, projectFonts, isLoading, isLoadingFonts } = useLoadedFilesAndFonts(filesPlan, fontsPlan, readsFonts);
    const loaded = useMemo(() => ({ ...pools, loadedFile, loadedFont, projectFonts }), [pools, loadedFile, loadedFont, projectFonts]);
    const isAwaitingFont = shapes.some(({ properties, shape }) => shape.awaitsFont?.(properties, loaded));
    useSceneFontSettleCount(isAwaitingFont);
    // The meshes of a context hold the metrics they read, so a context that read them before they
    // settled gives way to a copy, with meshes of its own, when they have.
    const context = useMemo(() => ({ ...loaded }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `isAwaitingFont` only re-keys the copy.
    [loaded, isAwaitingFont]);
    const isLoadingBuild = isLoading || isAwaitingFont;
    return useMemo(() => ({ context, isLoading: isLoadingBuild, isLoadingFonts }), [context, isLoadingBuild, isLoadingFonts]);
}
/** The shapes of `root` and of its CSG descendants, in tree order. Only CSG children join the root's boolean (`csgPlan.ts`). */
function subtreeShapes(root) {
    const planned = [];
    const visit = (node) => {
        const shape = nodeComponentRegistry.getCsgShape(node.type);
        if (!shape)
            return;
        planned.push({ properties: node.properties, shape });
        node.children.forEach(visit);
    };
    visit(root);
    return planned;
}
/** `paths` once each, sorted. */
const sortedUnique = (paths) => [...new Set(paths)].sort();
/** Each `.tres` a shape of the plan reads, sorted. */
function subtreeFiles(shapes, pools, loadedFile) {
    return sortedUnique(shapes.flatMap(({ properties, shape }) => shape.readsFiles?.(properties, pools, loadedFile) ?? []));
}
/** Each font file a shape of the plan reads, sorted. */
function subtreeFontFiles(shapes, ctx) {
    return sortedUnique(shapes.flatMap(({ properties, shape }) => shape.readsFonts?.(properties, ctx) ?? []));
}
//# sourceMappingURL=useCsgGeometryContext.js.map